import { Button } from "@/components/ui/button";
import { DialogFooter } from "@/components/ui/dialog";
import { Loader2, AlertTriangle } from "lucide-react";
import { useTranslations } from "use-intl";

interface BankStatusUnknownStateProps {
  /** Only ever rendered for these two — callers gate on `hasBankAccounts === "loading" || "error"`. */
  hasBankAccounts: "loading" | "error";
  onCancel: () => void;
}

/**
 * #498 whole-branch review finding I2 — shared across every "mark as received/paid/matched
 * externally" modal (payables, receivables, receipts): the system doesn't yet know whether
 * accounts are connected (still loading, or a fetch/consent error). Neither the audited
 * search/acknowledge flow NOR the trusted case-a date-picker may be reached from here — only
 * Cancel. This is the key invariant this state exists to protect: a consultant must never be
 * able to complete an unaudited externally-settled write while that's unknown.
 */
export default function BankStatusUnknownState({ hasBankAccounts, onCancel }: BankStatusUnknownStateProps) {
  const t = useTranslations("consultantMatching.shared");
  return (
    <div className="space-y-4">
      <div className="flex flex-col items-center justify-center gap-2 py-8 text-center text-sm text-muted-foreground">
        {hasBankAccounts === "loading" ? (
          <>
            <Loader2 className="h-6 w-6 animate-spin" />
            <span>{t("checkingAccounts")}</span>
          </>
        ) : (
          <>
            <AlertTriangle className="h-6 w-6 text-destructive" />
            <span>{t("unknownBank")}</span>
          </>
        )}
      </div>
      <DialogFooter>
        <Button variant="outline" onClick={onCancel}>{t("cancel")}</Button>
      </DialogFooter>
    </div>
  );
}
